import React, { FC, useState } from 'react';

import { useAsyncMethod } from '#hooks/useAsyncMethod';
import { useI18n } from '#hooks/useI18n';

import { ZazumeModal, ZZMForm } from '@zazume/zzm-base';
import { CurrencyVO, PriceVO } from '@zazume/zzm-utils';

import { useNotifications } from '#/hooks';
import { City } from '#/models/location/City';
import { CreateAlertModalBody } from '#/pages/investment/alerts/components/modal/CreateAlertModalBody';
import { CreateAlertModalFooter } from '#/pages/investment/alerts/components/modal/CreateAlertModalFooter';
import {
  getInvestmentAlertSchema,
  InvestmentAlertFormInputs
} from '#/pages/investment/alerts/components/modal/InvestmentAlert.schema';
import MetricsHelper from '#/utils/metrics/metricsHelper';
import { API } from '#lib/network/API';

type CreateAlertModalProps = {
  onClose?: () => void;
  onSubmit?: () => void;
};

const isCity = (value: any): value is City => {
  return value && value.id && value.name;
};

export const CreateAlertModal: FC<CreateAlertModalProps> = ({ onClose, onSubmit }) => {
  const { t } = useI18n();
  const { call } = useAsyncMethod();
  const formSchema = getInvestmentAlertSchema(t);
  const [city, setCity] = useState<City>();
  const { showNotification } = useNotifications();

  const handleSubmit = (formData: InvestmentAlertFormInputs) =>
    call(async () => {
      if (!isCity(city)) {
        return;
      }

      await API.investment.alerts.create({
        ...formData,
        location: {
          city
        },
        maxBudget: PriceVO.fromAmount(formData.maxBudget * 100, CurrencyVO.fromPrimitive('eur')).toPrimitive(),
        minRentPrice: PriceVO.fromAmount(formData.minRentPrice * 100, CurrencyVO.fromPrimitive('eur')).toPrimitive()
      });

      MetricsHelper.track('Add Investment Alert Completed');
      showNotification(t('investment.alertModal.success'), 'success');
      onSubmit?.();
      onClose?.();
    });

  return (
    <ZazumeModal title={t('investment.alertModal.title')} withCloseButton={false}>
      <ZZMForm.Form validation={formSchema} onSubmit={handleSubmit}>
        <CreateAlertModalBody setCity={setCity} />
        <CreateAlertModalFooter />
      </ZZMForm.Form>
    </ZazumeModal>
  );
};
